import React, { useCallback, useEffect, useRef } from "react";
import { useIntl } from "react-intl";

import {
    Form,
    Select,
    Input
} from "@gpstrackit/gtc-react-controls";
import { Divider } from "@mui/material";
import { useSelector } from "react-redux";
import { NotificationManager } from "Components/Notifications";

const isMissingOutputName = (nameValue) => nameValue == null || String(nameValue).trim() === '';

const isMissingOutputType = (typeValue) => {
    if (!typeValue) {
        return true;
    }

    if (typeof typeValue === 'object') {
        return !typeValue?.type;
    }

    return false;
};

/** A row field is required only when its sibling has a real value (empty clears it). */
export const isOutputNameRequired = (output) => !isMissingOutputType(output?.type);
export const isOutputTypeRequired = (output) => !isMissingOutputName(output?.name);

export const hasInvalidUnitOutputs = ({ outputsCounter, formData }) => {
    if (!Array.isArray(outputsCounter) || outputsCounter.length === 0 || !formData?.outputs) {
        return false;
    }

    return outputsCounter.some((position) => {
        const output = formData.outputs[position - 1];

        // A row is complete with both fields or empty with none; anything else is a half-filled row.
        return isMissingOutputName(output?.name) !== isMissingOutputType(output?.type);
    });
};

const InputOutputs = ({
    inputsCounter,
    outputsCounter,
    formData,
    setFormData,
    changes,
    setChanges,
    unitFormRef,
    setTab
}) => {
    const { messages } = useIntl();

    // Redux selectors
    const inputLabels = useSelector(state => state.catalogsRedux.inputLabels);
    const unit = useSelector((state) => state.fleetModalUpdateUnitRedux.unit);
    const itemRefs = useRef([]);

    const registerItem = useCallback((el) => {
        if (!el) return;
        if (!itemRefs.current.includes(el)) itemRefs.current.push(el);
    }, []);

    // Form.Item only re-validates on its own change, so a row keeps a stale error when the
    // sibling that made it required is the one that changed.
    useEffect(() => {
        itemRefs.current.forEach((r) => r?.validate && r.validate());
    }, [formData]);

    const didWrapSubmit = useRef(false);

    useEffect(() => {
        if (didWrapSubmit.current) return;
        if (!unitFormRef || typeof unitFormRef.submit !== "function") return;

        didWrapSubmit.current = true;
        const originalSubmit = unitFormRef.submit;

        unitFormRef.submit = (...args) => {
            itemRefs.current.forEach((r) => r?.validate && r.validate());

            const values = unitFormRef.ref?.current?.getValues?.() ?? {};
            if (hasInvalidUnitOutputs({ outputsCounter, formData, values })) {
                setTab?.(2);
                NotificationManager.info(
                    messages["fleetModalUpdateUnit_requiredFields"],
                    messages["fleetModalUpdateUnit_alert_title"],
                    5000
                );
                return false;
            }

            return originalSubmit(...args);
        };

        return () => {
            unitFormRef.submit = originalSubmit;
            didWrapSubmit.current = false;
        };
    }, [unitFormRef, formData, outputsCounter, setTab, messages]);

    //Options for output select
    const selectOutputOptions = [
        { type: "Toggle", label: messages["fleetModalUpdateUnit_toogle"] }
    ];

    if (unit?.type != "queclink") {
        selectOutputOptions.push({ type: "Pulse", label: messages["fleetModalUpdateUnit_pulse"] });
    }

    selectOutputOptions.push({ type: "singlePulse", label: messages["fleetModalUpdateUnit_single"] });

    return (
        <>
            {inputsCounter.map((i) => {
                return (
                    <Form.Item
                        name={`input${i}`}
                        label={messages["fleetModalUpdateUnit_inputLabel"] + " " + i}
                    >
                        <Select
                            id={`input${i}`}
                            placeholder={messages["select"]}
                            valueField="id"
                            textField="title"
                            style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                            isSearchable
                            isClearable
                            options={
                                inputLabels.filter(
                                    (nInput) =>
                                        formData.inputs?.filter(
                                            (fInput) =>
                                                nInput?.id === fInput?.labelId ||
                                                nInput?.id === fInput?.id
                                        ).length == 0
                                ) || null
                            }
                            onChange={(value) => {
                                let items = []
                                if (!value) {
                                    items = formData.inputs.filter(x => x.position != i)
                                }
                                else {
                                    items = formData.inputs.filter(x => x.position != i)
                                    items.push({ ...value, labelId: value.id, position: i })
                                }
                                setFormData({
                                    ...formData,
                                    inputs: items,
                                });
                            }}
                        />
                    </Form.Item>
                )
            })}
            {
                (!outputsCounter.length && !inputsCounter.length) &&
                <div style={{ textAlign: "center", opacity: 0.6 }}>
                    {messages['no_data']}
                </div>
            }
            {
                outputsCounter.length > 0 &&
                <div>
                    <label className="label-gtc-rc">{messages['fleetModalUpdateUnit_outputLabels']}</label>
                    <Divider style={{ marginBottom: 15 }} />
                </div>
            }
            {
                outputsCounter.map((i) => {
                    return (
                        <>
                            <Form.Item
                                ref={registerItem}
                                name={`output${i}`}
                                label={messages["fleetModalUpdateUnit_outputLabels"] + " " + i}
                                rules={[
                                    { required: !!formData?.outputs && isOutputNameRequired(formData.outputs[i - 1]), message: messages["required_field"] },
                                ]}
                            >
                                <Input
                                    id={`output${i}`}
                                    type="text"
                                    placeholder={messages["fleetModalUpdateUnit_outputLabels"] + " " + i}
                                    onChange={(value) => {
                                        setChanges({ ...changes, outputs: true });
                                        let copyOutputs = JSON.parse(
                                            JSON.stringify(formData?.outputs)
                                        );
                                        copyOutputs[i - 1] = {
                                            ...copyOutputs[i - 1],
                                            name: value,
                                        };
                                        setFormData({
                                            ...formData,
                                            outputs: copyOutputs,
                                        });
                                    }}
                                    maxLength={25}
                                />
                            </Form.Item>
                            <Form.Item
                                ref={registerItem}
                                name={`outputSelector${i}`}
                                label={messages['fleetModalUpdateUnit_assetType']}
                                rules={[
                                    { required: !!formData?.outputs && isOutputTypeRequired(formData.outputs[i - 1]), message: messages["required_field"] },
                                ]}
                            >
                                <Select
                                    id={`outputSelector${i}`}
                                    placeholder={messages['select']}
                                    valueField="type"
                                    textField="label"
                                    style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                                    isSearchable
                                    isClearable
                                    options={selectOutputOptions || []}
                                    onChange={(value) => {
                                        setChanges({ ...changes, outputs: true });
                                        let copyOutputs = JSON.parse(
                                            JSON.stringify(formData?.outputs)
                                        );
                                        copyOutputs[i - 1] = {
                                            ...copyOutputs[i - 1],
                                            type: value,
                                        };
                                        setFormData({
                                            ...formData,
                                            outputs: copyOutputs,
                                        });
                                    }}
                                />
                            </Form.Item>
                        </>
                    )
                })
            }
        </>
    );
}

export default InputOutputs;
